import React, { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Plus, X, BarChart3 } from "lucide-react"
import { usePolls } from "@/hooks/usePolls"

interface CreatePollModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  meetingId: string
}

export function CreatePollModal({ open, onOpenChange, meetingId }: CreatePollModalProps) {
  const { createPoll, isCreating } = usePolls(meetingId)
  const [question, setQuestion] = useState("")
  const [description, setDescription] = useState("")
  const [pollType, setPollType] = useState("single_choice")
  const [options, setOptions] = useState<string[]>(["", ""])
  const [isAnonymous, setIsAnonymous] = useState(false)
  const [allowAbstention, setAllowAbstention] = useState(true)

  const addOption = () => {
    setOptions([...options, ""])
  }

  const removeOption = (index: number) => {
    if (options.length > 2) {
      setOptions(options.filter((_, i) => i !== index))
    }
  }

  const updateOption = (index: number, value: string) => {
    const newOptions = [...options]
    newOptions[index] = value
    setOptions(newOptions)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!question.trim()) return
    
    const validOptions = options.filter(option => option.trim() !== "")
    if (validOptions.length < 2) return

    createPoll({
      question: question.trim(),
      description: description.trim() || undefined,
      poll_type: pollType,
      options: validOptions,
      is_anonymous: isAnonymous,
      allow_abstention: allowAbstention
    })

    // Reset form
    setQuestion("")
    setDescription("")
    setPollType("single_choice")
    setOptions(["", ""])
    setIsAnonymous(false)
    setAllowAbstention(true)
    onOpenChange(false)
  }

  const getPollTypeOptions = () => {
    return [
      { value: "single_choice", label: "Escolha única", description: "Participantes podem escolher apenas uma opção" },
      { value: "multiple_choice", label: "Múltipla escolha", description: "Participantes podem escolher várias opções" },
      { value: "yes_no", label: "Sim/Não", description: "Enquete simples de aprovação" }
    ]
  }

  // Auto-generate Yes/No options when poll type changes
  React.useEffect(() => {
    if (pollType === "yes_no") {
      setOptions(["Sim", "Não"])
    } else if (pollType !== "yes_no" && options.length === 2 && options[0] === "Sim" && options[1] === "Não") {
      setOptions(["", ""])
    }
  }, [pollType])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6" />
            Nova Enquete
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Question */}
          <div className="space-y-2">
            <Label htmlFor="question">Pergunta *</Label>
            <Input
              id="question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Ex: Vocês aprovam a nova proposta de taxa condominial?"
              required
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Descrição (opcional)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Adicione contexto ou detalhes sobre a enquete..."
              rows={2}
            />
          </div>

          {/* Poll Type */}
          <div className="space-y-2">
            <Label>Tipo de Enquete</Label>
            <Select value={pollType} onValueChange={setPollType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {getPollTypeOptions().map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    <div>
                      <div className="font-medium">{option.label}</div>
                      <div className="text-sm text-muted-foreground">{option.description}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Options */}
          <div className="space-y-2">
            <Label>Opções de Resposta *</Label>
            <div className="space-y-3">
              {options.map((option, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="flex-1">
                    <Input
                      value={option}
                      onChange={(e) => updateOption(index, e.target.value)}
                      placeholder={`Opção ${index + 1}`}
                      disabled={pollType === "yes_no"}
                      required
                    />
                  </div>
                  {pollType !== "yes_no" && options.length > 2 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeOption(index)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              
              {pollType !== "yes_no" && options.length < 6 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={addOption}
                  className="w-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Opção
                </Button>
              )}
            </div>
          </div>

          {/* Settings */}
          <div className="space-y-4 border-t pt-4">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
              Configurações
            </h3>
            
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <input
                  id="is_anonymous"
                  type="checkbox"
                  checked={isAnonymous}
                  onChange={(e) => setIsAnonymous(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="is_anonymous" className="text-sm font-normal">
                  Enquete anônima
                </Label>
              </div>
              <p className="text-xs text-muted-foreground ml-6">
                Os votos não serão associados aos nomes dos participantes
              </p>

              <div className="flex items-center space-x-2">
                <input
                  id="allow_abstention"
                  type="checkbox"
                  checked={allowAbstention}
                  onChange={(e) => setAllowAbstention(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="allow_abstention" className="text-sm font-normal">
                  Permitir abstenção
                </Label>
              </div>
              <p className="text-xs text-muted-foreground ml-6">
                Participantes poderão se abster de votar
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button 
              type="submit" 
              disabled={isCreating || !question.trim() || options.filter(o => o.trim()).length < 2}
              className="bg-primary hover:bg-primary/90"
            >
              {isCreating ? "Criando..." : "Criar Enquete"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}